import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Activity,
  CalendarIcon,
  Search,
  Filter,
  X,
  PlusCircle,
  Trash2,
  CheckCircle,
  XCircle,
  Edit2,
  Undo2,
  UserCog,
  FileText,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_ICONS = {
  ticket_created: { icon: PlusCircle, color: 'text-green-600', bg: 'bg-green-50' },
  ticket_updated: { icon: Edit2, color: 'text-blue-600', bg: 'bg-blue-50' },
  ticket_deleted: { icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' },
  lab_approved: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  lab_rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  lab_revert: { icon: Undo2, color: 'text-amber-600', bg: 'bg-amber-50' },
  stock_entered: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
  accounts_revert: { icon: Undo2, color: 'text-amber-600', bg: 'bg-amber-50' },
  role_changed: { icon: UserCog, color: 'text-purple-600', bg: 'bg-purple-50' },
};

const ACTION_LABELS = {
  ticket_created: 'Created Ticket',
  ticket_updated: 'Updated Ticket',
  ticket_deleted: 'Deleted Ticket',
  lab_approved: 'Lab Approved',
  lab_rejected: 'Lab Rejected',
  lab_revert: 'Lab Reverted',
  stock_entered: 'Stock Entered',
  accounts_revert: 'Accounts Reverted',
  role_changed: 'Role Changed',
};

export default function ActivityLog() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [action, setAction] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (action && action !== 'all') params.append('action', action);
      if (startDate) params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      params.append('limit', limit.toString());
      params.append('skip', ((page - 1) * limit).toString());

      const response = await fetch(`${API_URL}/api/activity-logs?${params.toString()}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      } else if (response.status === 403) {
        toast.error('Access denied - Manager only');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [action, startDate, endDate, page, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/activity-logs/stats`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      if (user.role !== 'manager') {
        toast.error('Access denied');
        navigate('/dashboard');
        return;
      }
      fetchLogs();
      fetchStats();
    }
  }, [authLoading, user, fetchLogs, fetchStats, navigate]);

  const clearFilters = () => {
    setAction('all');
    setStartDate(null);
    setEndDate(null);
    setPage(1);
  };

  const getActionDisplay = (actionType) => {
    const config = ACTION_ICONS[actionType] || { icon: Activity, color: 'text-slate-600', bg: 'bg-slate-50' };
    const Icon = config.icon;
    return (
      <div className={`w-10 h-10 ${config.bg} rounded-full flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${config.color}`} />
      </div>
    );
  };

  const formatDetails = (log) => {
    const details = log.details || {};
    const parts = [];
    
    if (details.batch_number) parts.push(`Batch: ${details.batch_number}`);
    if (details.product_name) parts.push(`Product: ${details.product_name}`);
    if (details.quantity) parts.push(`Qty: ${details.quantity}`);
    if (details.new_role) parts.push(`New Role: ${details.new_role}`);
    if (details.target_user) parts.push(`User: ${details.target_user}`);
    if (details.reverted_from) parts.push(`Reverted from: ${details.reverted_from}`);
    
    return parts.join(' • ');
  };

  const totalPages = Math.ceil(total / limit);

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="activity-log-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="h-10 w-10 p-0"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Activity Log</h1>
            <p className="text-slate-500 mt-1">Track all user activities and changes</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.today_count || 0}</p>
                  <p className="text-sm text-slate-500">Today's Activities</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{total}</p>
                  <p className="text-sm text-slate-500">Total Logs</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <UserCog className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.user_activity?.length || 0}</p>
                  <p className="text-sm text-slate-500">Active Users</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger className="h-12" data-testid="action-filter">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="ticket_created">Ticket Created</SelectItem>
                  <SelectItem value="ticket_updated">Ticket Updated</SelectItem>
                  <SelectItem value="ticket_deleted">Ticket Deleted</SelectItem>
                  <SelectItem value="lab_approved">Lab Approved</SelectItem>
                  <SelectItem value="lab_rejected">Lab Rejected</SelectItem>
                  <SelectItem value="lab_revert">Lab Reverted</SelectItem>
                  <SelectItem value="stock_entered">Stock Entered</SelectItem>
                  <SelectItem value="accounts_revert">Accounts Reverted</SelectItem>
                  <SelectItem value="role_changed">Role Changed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="h-12 px-4"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Date Range
              {(startDate || endDate) && (
                <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full" />
              )}
            </Button>

            <Button
              onClick={fetchLogs}
              className="h-12 px-6 bg-slate-900 hover:bg-slate-800"
              data-testid="search-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">From Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-12 w-full justify-start text-left font-normal"
                      data-testid="start-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">To Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-12 w-full justify-start text-left font-normal"
                      data-testid="end-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex items-end">
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="h-12 w-full"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Activity List */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="activity-list">
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No activity logs found</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-100">
                {logs.map((log) => (
                  <div
                    key={log.activity_id}
                    className="p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`log-${log.activity_id}`}
                  >
                    <div className="flex items-start gap-4">
                      {getActionDisplay(log.action)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">
                            {log.user_name}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full capitalize">
                            {log.user_role}
                          </span>
                          <span className="text-slate-500">•</span>
                          <span className="text-sm font-medium text-slate-700">
                            {ACTION_LABELS[log.action] || log.action}
                          </span>
                        </div>
                        
                        {log.entity_id && (
                          <p className="text-sm text-slate-600 mt-1">
                            <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                              {log.entity_id}
                            </span>
                          </p>
                        )}
                        
                        {formatDetails(log) && (
                          <p className="text-sm text-slate-500 mt-1">
                            {formatDetails(log)}
                          </p>
                        )}
                      </div>
                      
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm text-slate-500">
                          {new Date(log.timestamp).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-slate-400">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <span className="text-sm text-slate-500">
                    Page {page} of {totalPages} ({total} total)
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
